/**
 * AdminSales — bucket 2 of 6. National ticket-sales view matching
 * `admin-sales.jsx`. Live-wired to trailing-6-week Cube rollups:
 *
 *   - 4-up KPI strip: Weekly ticket sales (National / Region scope
 *     toggle), Tickets sold · 6 wks, Ad spend · week, Conversion.
 *   - LEFT: National Trend sparkline (Actual only) + Revenue by Region.
 *   - RIGHT: National Channel Mix + Regional Pace (derived sold/cap).
 *
 * Any KPI / module whose backend source is unavailable renders an
 * em-dash / empty state (the data is wired; it's just unavailable at
 * runtime). No MockedOverlay — only the trend Forecast line lacks a
 * source, handled by omitting it.
 */
import { useState } from 'react';

import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, SegmentedControl } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';

import { useAdminSales, type AdminSalesDto, type KpiCell } from './adminApi';
import { AdminSalesChannelMix } from './AdminSalesChannelMix';
import { AdminSalesPace } from './AdminSalesPace';
import { AdminSalesRegionBars } from './AdminSalesRegionBars';
import { AdminSalesTrend } from './AdminSalesTrend';

const EM_DASH = '—';

type Scope = 'National' | 'Region';

const SCOPE_OPTIONS: ReadonlyArray<{ value: Scope; label: string }> = [
  { value: 'National', label: 'National' },
  { value: 'Region', label: 'Region' },
];

function fmtDollars(cents: number): string {
  const dollars = Math.round(cents / 100);
  if (dollars >= 10_000) return `$${(dollars / 1000).toFixed(1)}k`;
  return `$${dollars.toLocaleString('en-US')}`;
}

/** The top region by trailing-6wk revenue (rows arrive pre-sorted). */
function topRegion(
  rows: AdminSalesDto['revenueByRegion'],
): AdminSalesDto['revenueByRegion'][number] | null {
  return rows[0] ?? null;
}

/**
 * Recompute the leading "Weekly ticket sales" KPI for the selected scope:
 * National = this-week national revenue; Region = the top region's
 * trailing-6wk revenue with its name as the sub-label.
 */
function weeklyKpiForScope(data: AdminSalesDto, scope: Scope): KpiCell {
  const base = data.kpis[0];
  if (scope === 'National') return base;
  const top = topRegion(data.revenueByRegion);
  if (!top) return { label: base.label, value: EM_DASH };
  return { label: base.label, value: fmtDollars(top.revenueCents) };
}

function ScopedKpiStrip({ data, scope }: { data: AdminSalesDto; scope: Scope }) {
  const kpis = [weeklyKpiForScope(data, scope), ...data.kpis.slice(1)];
  return <KpiStrip items={adaptKpis(kpis)} />;
}

function ScopeToggle({ scope, onChange }: { scope: Scope; onChange: (s: Scope) => void }) {
  const top = scope === 'Region' ? 'top region · 6 wks' : 'this week, all regions';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <span className="nb-eyebrow" style={{ fontSize: 10 }}>
        Weekly ticket sales — {top}
      </span>
      <SegmentedControl
        value={scope}
        onChange={onChange}
        options={SCOPE_OPTIONS}
        ariaLabel="Sales scope"
      />
    </div>
  );
}

function SalesBody({ data }: { data: AdminSalesDto }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        <AdminSalesTrend trend={data.nationalTrend} />
        <AdminSalesRegionBars rows={data.revenueByRegion} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        <AdminSalesChannelMix mix={data.channelMix} />
        <AdminSalesPace rows={data.revenueByRegion} />
      </div>
    </div>
  );
}

export default function AdminSales() {
  const [scope, setScope] = useState<Scope>('National');
  const q = useAdminSales();
  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ScopeToggle scope={scope} onChange={setScope} />
      <ScopedKpiStrip data={data} scope={scope} />
      <SalesBody data={data} />
    </div>
  );
}
